(() => {
  if (window.AuremeAccountEntry) return;

  const DEFAULT_THEME_LOGIN_REDIRECT_PATH = '/account';
  const THEME_ACCOUNT_LOGIN_PATH = '/account/login';
  const CHECKOUT_URL_PARAM = 'checkout_url';
  const PATH_REDIRECT_PARAMS = ['redirect_url', 'return_url'];
  const ACCOUNT_ENTRY_LINK_SELECTOR = 'a[data-aureme-account-entry-link]';
  const ACCOUNT_ENTRY_CONTAINER_SELECTOR = '[data-aureme-account-entry-container]';
  const ACCOUNT_ENTRY_TARGET_SELECTOR = `${ACCOUNT_ENTRY_LINK_SELECTOR}, ${ACCOUNT_ENTRY_CONTAINER_SELECTOR}`;
  const THEME_LOGIN_PAGE_SELECTOR = '[data-aureme-account-entry-login-page]';
  // URL 正規化は theme.liquid で先行読み込みする aureme-url.js に集約する。
  const auremeUrl = window.AuremeUrl;
  if (!auremeUrl) return;
  let loginRedirectHandled = false;
  let refreshScheduled = false;
  let accountEntryObserver = null;
  const pendingRefreshRoots = new Set();

  function getAccountEntryConfig() {
    return window.Aureme?.config || {};
  }

  function getWishlistLoginReturn() {
    return window.AuremeWishlistLoginReturn || {};
  }

  function removeWishlistReturnTo() {
    const { removeReturnTo } = getWishlistLoginReturn();
    if (typeof removeReturnTo === 'function') removeReturnTo();
  }

  function getWishlistReturnTo() {
    const { getReturnTo } = getWishlistLoginReturn();
    return typeof getReturnTo === 'function' ? getReturnTo() : '';
  }

  function readDecodedSearchParam(searchParams, key) {
    const value = searchParams.get(key);
    if (!value) return '';

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  function hasExplicitRedirectValue(searchParams) {
    if (readDecodedSearchParam(searchParams, CHECKOUT_URL_PARAM)) return true;

    return PATH_REDIRECT_PARAMS.some((param) => readDecodedSearchParam(searchParams, param));
  }

  /* /account/login 直アクセス時の転送先は checkout_url を最優先で解決する */
  function resolveThemeLoginRedirectValue(
    search = window.location.search,
    fallbackPath = getAccountEntryConfig().accountUrl || DEFAULT_THEME_LOGIN_REDIRECT_PATH,
  ) {
    const { cartUrl } = getAccountEntryConfig();
    const searchParams = new URLSearchParams(search);
    const decodedCheckoutUrl = readDecodedSearchParam(searchParams, CHECKOUT_URL_PARAM);

    if (decodedCheckoutUrl) {
      if (decodedCheckoutUrl.includes('/checkouts')) {
        return cartUrl || '/cart';
      }

      return auremeUrl.normalizeRedirectPath(decodedCheckoutUrl, fallbackPath);
    }

    for (const param of PATH_REDIRECT_PARAMS) {
      const decodedPath = readDecodedSearchParam(searchParams, param);
      if (decodedPath) {
        return auremeUrl.normalizeRedirectPath(decodedPath, fallbackPath);
      }
    }

    const wishlistReturnTo = getWishlistReturnTo();
    if (wishlistReturnTo) return wishlistReturnTo;

    return fallbackPath;
  }

  function buildCustomAppLoginUrl(customAppDomain, redirectValue) {
    if (!customAppDomain) {
      return (
        getAccountEntryConfig().accountLoginUrl ||
        window.routes?.account_login_url ||
        THEME_ACCOUNT_LOGIN_PATH
      );
    }

    return redirectValue
      ? `${customAppDomain}/account/login?redirect_url=${redirectValue}`
      : `${customAppDomain}/account/login`;
  }

  function buildAccountEntryUrl(options = {}) {
    const config = getAccountEntryConfig();

    if (config.customerLoggedIn) {
      return config.accountUrl || DEFAULT_THEME_LOGIN_REDIRECT_PATH;
    }

    const customAppDomain = options.customAppDomain || config.customAppDomain || '';
    const fallbackRedirectPath =
      options.fallbackRedirectPath || config.accountUrl || DEFAULT_THEME_LOGIN_REDIRECT_PATH;
    const redirectValue =
      options.redirectValue ||
      auremeUrl.normalizeRedirectPath(
        options.rawRedirectPath || window.location.href,
        fallbackRedirectPath,
      );

    return buildCustomAppLoginUrl(customAppDomain, redirectValue);
  }

  function isCustomAppLoginUrl(href, customAppDomain) {
    if (!href || !customAppDomain) return false;

    try {
      const hrefUrl = new URL(href, window.location.origin);
      const customAppUrl = new URL(customAppDomain, window.location.origin);
      const customAppLoginPath = `${customAppUrl.pathname.replace(/\/$/, '')}/account/login`;

      return hrefUrl.origin === customAppUrl.origin && hrefUrl.pathname === customAppLoginPath;
    } catch {
      const loginUrl = `${customAppDomain}/account/login`;
      return href === loginUrl || href.startsWith(`${loginUrl}?`);
    }
  }

  /*
    コンテナ指定では app 側ログイン URL とテーマ標準 URL が混在するため、
    href の文字列ではなく pathname 契約で会員導線だけを抽出する。
  */
  function isAccountEntryContainerLink(link) {
    const href = link.getAttribute('href') || '';
    const resolvedHref = link.href || href;
    const config = getAccountEntryConfig();
    const customAppDomain = config.customAppDomain || '';

    if (isCustomAppLoginUrl(resolvedHref, customAppDomain)) {
      return true;
    }

    return [
      config.accountLoginUrl,
      config.accountUrl,
      window.routes?.account_login_url,
      THEME_ACCOUNT_LOGIN_PATH,
      DEFAULT_THEME_LOGIN_REDIRECT_PATH,
    ].some((path) => auremeUrl.isSamePathname(resolvedHref, path));
  }

  function addAccountEntryContainerLinks(container, links) {
    for (const link of container.querySelectorAll('a')) {
      if (isAccountEntryContainerLink(link)) {
        links.add(link);
      }
    }
  }

  function collectAccountEntryLinks(root = document) {
    const links = new Set();
    const searchRoot = root instanceof Element || root instanceof Document ? root : document;

    if (searchRoot instanceof Element) {
      if (searchRoot.matches(ACCOUNT_ENTRY_LINK_SELECTOR)) {
        links.add(searchRoot);
      }

      if (searchRoot.matches(ACCOUNT_ENTRY_CONTAINER_SELECTOR)) {
        addAccountEntryContainerLinks(searchRoot, links);
      }
    }

    for (const link of searchRoot.querySelectorAll(ACCOUNT_ENTRY_LINK_SELECTOR)) {
      links.add(link);
    }

    for (const container of searchRoot.querySelectorAll(ACCOUNT_ENTRY_CONTAINER_SELECTOR)) {
      addAccountEntryContainerLinks(container, links);
    }

    return links;
  }

  function hasAccountEntryTarget(root) {
    if (!(root instanceof Element)) return false;

    return (
      root.matches(ACCOUNT_ENTRY_LINK_SELECTOR) ||
      root.matches(ACCOUNT_ENTRY_CONTAINER_SELECTOR) ||
      Boolean(root.querySelector(ACCOUNT_ENTRY_TARGET_SELECTOR))
    );
  }

  function findAccountEntryRefreshRoot(node) {
    if (!(node instanceof Element)) return null;
    if (hasAccountEntryTarget(node)) return node;

    return node.closest(ACCOUNT_ENTRY_CONTAINER_SELECTOR);
  }

  function handleWishlistReturnToAfterLogin() {
    const { handleAfterLogin } = getWishlistLoginReturn();
    return typeof handleAfterLogin === 'function' ? handleAfterLogin() : false;
  }

  function refreshAccountEntryLinks(root = document) {
    const accountEntryUrl = buildAccountEntryUrl();

    for (const link of collectAccountEntryLinks(root)) {
      link.href = accountEntryUrl;
    }
  }

  function redirectThemeLoginPage() {
    if (loginRedirectHandled) return;

    const redirectElement = document.querySelector(THEME_LOGIN_PAGE_SELECTOR);
    if (!(redirectElement instanceof HTMLElement)) return;

    const config = getAccountEntryConfig();
    const customAppDomain = config.customAppDomain || '';
    if (!customAppDomain) return;

    const fallbackRedirectPath = config.accountUrl || DEFAULT_THEME_LOGIN_REDIRECT_PATH;
    const searchParams = new URLSearchParams(window.location.search);

    /*
     * checkout_url / redirect_url / return_url が明示されたログインでは、その値を優先する。
     * 直前の wishlist 復帰先を残すと checkout や return_url の遷移を上書きしてしまう。
     */
    if (hasExplicitRedirectValue(searchParams)) {
      removeWishlistReturnTo();
    }

    const redirectValue = resolveThemeLoginRedirectValue(
      window.location.search,
      fallbackRedirectPath,
    );
    const redirectUrl = buildAccountEntryUrl({ customAppDomain, redirectValue });

    loginRedirectHandled = true;
    location.replace(redirectUrl);
  }

  function initAccountEntry() {
    if (handleWishlistReturnToAfterLogin()) return;

    refreshAccountEntryLinks(document);
    redirectThemeLoginPage();
  }

  /*
   * 外部アプリや section 再描画で後挿入される会員導線を、
   * 1 フレームに集約して現在の状態に応じた entry URL へ張り替える。
   */
  function scheduleRefresh(root = document) {
    pendingRefreshRoots.add(root);

    if (refreshScheduled) return;
    refreshScheduled = true;

    const callback = () => {
      const refreshRoots = Array.from(pendingRefreshRoots);
      pendingRefreshRoots.clear();
      refreshScheduled = false;

      for (const refreshRoot of refreshRoots) {
        refreshAccountEntryLinks(refreshRoot);
      }
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback);
    } else {
      setTimeout(callback, 0);
    }
  }

  function observeAccountEntryLinks() {
    if (!document.body || typeof MutationObserver !== 'function' || accountEntryObserver) return;

    accountEntryObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          const refreshRoot = findAccountEntryRefreshRoot(node);
          if (refreshRoot) {
            scheduleRefresh(refreshRoot);
          }
        }
      }
    });

    accountEntryObserver.observe(document.body, { childList: true, subtree: true });
  }

  function disconnectAccountEntryObserver() {
    accountEntryObserver?.disconnect();
    accountEntryObserver = null;
    pendingRefreshRoots.clear();
  }

  /*
   * bfcache 復帰時は pagehide で切った observer を戻す前に、
   * 既存導線を全体同期する。
   */
  function restoreAccountEntryObserver() {
    refreshAccountEntryLinks(document);
    observeAccountEntryLinks();
  }

  window.AuremeAccountEntry = {
    normalizeRedirectPath: auremeUrl.normalizeRedirectPath,
    resolveThemeLoginRedirectValue,
    buildCustomAppLoginUrl,
    buildAccountEntryUrl,
    refreshAccountEntryLinks,
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAccountEntry();
        observeAccountEntryLinks();
      },
      { once: true },
    );
  } else {
    initAccountEntry();
    observeAccountEntryLinks();
  }

  document.addEventListener('shopify:section:load', (event) => {
    refreshAccountEntryLinks(event.target);
  });

  window.addEventListener('pagehide', disconnectAccountEntryObserver);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    restoreAccountEntryObserver();
  });
})();
